export const MESH_POINT_TYPE = 'mesh';

const BSSID_HEATMAP_IMAGE_TYPE = 'bssid_heatmap';

export interface MeshCapablePointOfInterest {
    label?: string;
    color: string;
    type?: string;
    coordinates?: {x: number; y: number};
    metrics?: {bssid?: string; rssi?: number; ssid?: string; frequency?: string};
}

export interface MeshFrequencySource {
    type: string;
    bssid?: string;
    frequency?: string;
}

export interface MarkerLegendDisplayItem {
    label: string;
    color: string;
    isMesh: boolean;
}

export const MAX_LABEL_LENGTH = 25;

export const truncateLabel = (label: string): string =>
    label.length > MAX_LABEL_LENGTH ? `${label.slice(0, MAX_LABEL_LENGTH)}...` : label;

export const isMeshPoint = (poi: MeshCapablePointOfInterest): boolean => poi.type === MESH_POINT_TYPE;

export const isCapturedPoint = (poi: MeshCapablePointOfInterest): boolean =>
    typeof poi.coordinates?.x === 'number' && typeof poi.coordinates?.y === 'number';

/** Mesh points that were not captured during the scan must not appear on the map or in the legend. */
export const filterCapturedPointsOfInterest = <T extends MeshCapablePointOfInterest>(pointsOfInterest: T[]): T[] =>
    pointsOfInterest.filter((poi) => !isMeshPoint(poi) || isCapturedPoint(poi));

export const getMeshPointFrequency = (
    poi: MeshCapablePointOfInterest,
    imageUrls: MeshFrequencySource[] = []
): string | undefined => {
    if (poi.metrics?.frequency) {
        return poi.metrics.frequency;
    }

    const bssid = poi.metrics?.bssid?.toLowerCase();

    if (!bssid) {
        return undefined;
    }

    return imageUrls.find(
        (img) => img.type === BSSID_HEATMAP_IMAGE_TYPE && img.bssid?.toLowerCase() === bssid && img.frequency
    )?.frequency;
};

/** The `(frequency)` suffix is appended after truncation so it always renders in full. */
export const getMeshPointLabel = (poi: MeshCapablePointOfInterest, meshIndex: number, frequency?: string): string => {
    const name = truncateLabel(poi.label || `Mesh Point ${meshIndex}`);

    return frequency ? `${name} (${frequency})` : name;
};

export const buildMarkersLegendItems = (
    pointsOfInterest: MeshCapablePointOfInterest[] = [],
    imageUrls: MeshFrequencySource[] = []
): MarkerLegendDisplayItem[] => {
    let meshIndex = 0;

    return filterCapturedPointsOfInterest(pointsOfInterest)
        .filter((poi) => !!poi.type)
        .map((poi) => {
            if (!isMeshPoint(poi)) {
                return {label: truncateLabel(poi.label || ''), color: poi.color, isMesh: false};
            }

            meshIndex += 1;

            return {
                label: getMeshPointLabel(poi, meshIndex, getMeshPointFrequency(poi, imageUrls)),
                color: poi.color,
                isMesh: true
            };
        });
};
